/** Utterances shorter than this wait for transcription before response.create. */
export const SHORT_UTTERANCE_MS = 2000;

/**
 * Extra wait after the speech-stop grace period for a short-utterance transcript.
 * If transcription never arrives, the turn is dropped (conservative).
 */
export const TURN_GATE_TIMEOUT_PAD_MS = 1_200;

/**
 * Matches elongated filler tokens (hm/hmm/uh/um/mmm/…).
 * Applied per whitespace-separated token after ASCII normalize.
 */
const FILLER_TOKEN_RE =
  /^(?:u+h+m*|u+m+|h+m+|m+h*m*|a+h+|o+h+|e+r+m*|e+r+|h+u+h+)$/;

function isFillerNormalized(normalized: string): boolean {
  const tokens = normalized.split(' ').filter(Boolean);
  return tokens.length > 0 && tokens.every((t) => FILLER_TOKEN_RE.test(t));
}

/**
 * Rule-based check for whether a short utterance has enough linguistic content
 * to justify an AI response. Empty / non-Latin / pure filler → false.
 *
 * This is a turn-validity gate only — not a substantive-answer completeness check.
 */
export function hasUsableTranscriptContent(raw: string): boolean {
  const text = raw.trim();
  if (!text) return false;

  // English-only interviews: reject ASR noise hallucinations with no Latin letters
  // (e.g. "와.", "음", "嗯").
  if (!/[A-Za-z]/.test(text)) return false;

  // ASCII normalize is enough: non-Latin-only strings already failed above.
  const normalized = text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return false;
  if (isFillerNormalized(normalized)) return false;

  return true;
}
